import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { FiStar } from 'react-icons/fi'
import { usePublicContent } from '../hooks/usePublicContent'
import { TestimonialsContent } from '../types/content'

const Testimonials: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const { content } = usePublicContent('testimonials')
  const testimonialsData = content?.data as TestimonialsContent

  const testimonials = testimonialsData?.testimonials || []

  if (testimonials.length === 0) return null

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center space-x-1 mb-3">
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar
            key={star}
            className={`${
              star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-neutral-300'
            }`}
            size={18}
          />
        ))}
      </div>
    )
  }

  return (
    <section className="section-padding bg-neutral-50">
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16 px-4"
        >
          <h2 className="font-bold text-neutral-800 mb-4 sm:mb-6" style={{ fontSize: 'clamp(28px, 4vw, 48px)' }}>
            {testimonialsData?.title || 'What Our Clients Say'}
          </h2>
          {testimonialsData?.subtitle && (
            <p className="body-longform text-neutral-600 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed">
              {testimonialsData.subtitle}
            </p>
          )}
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...testimonials]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .slice(0, 4)
            .map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl p-6 sm:p-8 transition-all duration-300 hover:-translate-y-2"
              >
                {renderStars(testimonial.rating)}

                <p className="text-neutral-600 mb-6 italic leading-relaxed text-sm sm:text-base">
                  "{testimonial.reviewText}"
                </p>

                <div>
                  <p className="font-bold text-lg text-neutral-800">
                    {testimonial.reviewerName}
                  </p>
                  {testimonial.date && (
                    <p className="text-xs text-neutral-500 mt-1">
                      {testimonial.date}
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
        </div>
      </div>
    </section>
  )
}

export default Testimonials
